// API service for handling approval-related requests
export interface ApprovalRequest {
  requestId: string;
  action: 'approve' | 'reject';
  approverEmail: string;
  rejectionReason?: string;
  notes?: string;
  BNAME?: string; // SAP Username (required by backend)
  ROLE_NAME?: string; // Role Name (required by backend)
}

export interface ApprovalResponse {
  success: boolean;
  message: string;
  executionResult?: {
    success: boolean;
    message: string;
    sapResponse?: string;
  };
}

export interface RoleRequestDetails {
  RequestId: string;
  RequestType: string;
  UserDisplayName: string;
  SapUsername: string;
  RoleName: string;
  OldRoleName?: string;
  RequestedBy: string;
  RequestedDate: string;
  Justification: string;
  ManagerName: string;
  ManagerEmail: string;
  Status?: string; // Optional since pending approvals don't have status
}

class ApprovalAPI {
  private baseUrl: string;
  private functionKey: string | null;

  constructor() {
    // Update this to your actual function app URL
    this.baseUrl = process.env.REACT_APP_FUNCTION_URL || 'https://af-xen-eu-nyx-prd-gtc0gbh7bxazhnec.westeurope-01.azurewebsites.net/api';
    // Function key for Azure Functions authentication (optional for local dev)
    this.functionKey = process.env.REACT_APP_FUNCTION_KEY || null;
  }

  /**
   * Get headers for API requests, including function key if available
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add function key as query parameter if provided (Azure Functions standard)
    return headers;
  }

  /**
   * Build URL with function key if provided
   */
  private buildUrl(endpoint: string): string {
    const url = `${this.baseUrl}/${endpoint}`;
    if (this.functionKey) {
      return `${url}?code=${this.functionKey}`;
    }
    return url;
  }

  /**
   * Process an approval or rejection
   */
  async processApproval(request: ApprovalRequest): Promise<ApprovalResponse> {
    try {
      const response = await fetch(this.buildUrl('function_process_approval'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Normalize the response to match frontend expectations
      return {
        success: result.Success || false,
        message: result.Message || 'Request processed',
        executionResult: result.ExecutionResult
      };
    } catch (error) {
      console.error('Error processing approval:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Get request details by ID
   */
  async getRequestDetails(requestId: string): Promise<RoleRequestDetails | null> {
    try {
      const response = await fetch(this.buildUrl('function_get_pending_approvals'), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Find the specific request by ID
      if (result.Success && result.Requests) {
        const request = result.Requests.find((req: any) => req.RequestId === requestId);
        return request || null;
      }

      return null;
    } catch (error) {
      console.error('Error fetching request details:', error);
      return null;
    }
  }

  /**
   * Get all pending approvals
   */
  async getPendingApprovals(): Promise<RoleRequestDetails[]> {
    try {
      const response = await fetch(this.buildUrl('function_get_pending_approvals'), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.Success && result.Requests) {
        return result.Requests;
      }

      return [];
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      return [];
    }
  }


  /**
   * Search for user by email using Lakeland connection
   */
  async searchUserByEmail(email: string): Promise<{
    Success: boolean;
    Message: string;
    UserEmail?: string; // For "notfound" cases
    UserDetails?: {
      Id: string;
      DisplayName: string;
      Email: string;
      JobTitle: string;
      Department: string;
      OfficeLocation: string;
      MobilePhone: string;
      BusinessPhones: string;
      SapUsername: string;
    };
    ManagerDetails?: {
      Id: string;
      DisplayName: string;
      Email: string;
      JobTitle: string;
      Department: string;
      OfficeLocation: string;
      MobilePhone: string;
      BusinessPhones: string;
      SapUsername: string;
    };
  }> {
    try {
      const response = await fetch(this.buildUrl('function_nyx_lakeland_connection'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ email }),
      });

      // Parse the response body regardless of status code
      // The API returns JSON even for error cases (404, etc.)
      const result = await response.json();
      
      // If the API returned Success: false, return it as-is (it contains the error message)
      if (!result.Success) {
        return result;
      }
      
      // If response is not ok but we got a result, return it
      if (!response.ok) {
        return {
          Success: false,
          Message: result.Message || `HTTP error! status: ${response.status}`,
        };
      }

      return result;
    } catch (error) {
      console.error('Error searching user:', error);
      // If we can't parse the response, return a generic error
      return {
        Success: false,
        Message: error instanceof Error ? error.message : 'Failed to search for user. Please try again.',
      };
    }
  }

  /**
   * Get all requests (both open and closed)
   */
  async getAllRequests(): Promise<RoleRequestDetails[]> {
    try {
      const response = await fetch(this.buildUrl('function_get_all_requests'), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.Success && result.Requests) {
        return result.Requests;
      }

      return [];
    } catch (error) {
      console.error('Error fetching all requests:', error);
      return [];
    }
  }

  /**
   * Send approval request
   */
  async sendApprovalRequest(requestData: {
    requestType: string;
    userId: string;
    userDisplayName: string;
    sapUsername: string;
    roleId: string;
    roleName: string;
    oldRoleId?: string;
    oldRoleName?: string;
    requestedBy: string;
    justification: string;
    managerEmail: string;
    managerName: string;
  }): Promise<{ 
    success: boolean; 
    message: string; 
    requestId?: string;
    approvalStatus?: string;
    sentTo?: string[];
    approvalUrl?: string;
  }> {
    try {
      const response = await fetch(this.buildUrl('function_nyx_sendapproval'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Normalize the response to match frontend expectations
      return {
        success: result.Success || false,
        message: result.Message || 'Request processed',
        requestId: result.RequestId,
        approvalStatus: result.ApprovalStatus,
        sentTo: result.SentTo,
        approvalUrl: result.ApprovalUrl
      };
    } catch (error) {
      console.error('Error sending approval request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Get current SAP roles for a user
   */
  async getCurrentSapRoles(sapId: string): Promise<{
    Success: boolean;
    SapUserId: string;
    RoleCount: number;
    Roles: Array<{
      agrName?: string;
      RoleName?: string;
      fromDat?: string;
      toDat?: string;
      [key: string]: any;
    }> | {
      [key: string]: string;
    };
  }> {
    try {
      const response = await fetch(this.buildUrl('function_sap_currentroles'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ sapid: sapId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching current SAP roles:', error);
      return {
        Success: false,
        SapUserId: sapId,
        RoleCount: 0,
        Roles: {}
      };
    }
  }
}

// Export a singleton instance
export const approvalAPI = new ApprovalAPI();
export default approvalAPI;